import { create } from 'zustand'
import { persist, createJSONStorage, devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { sessionCartService } from '@/services'
import { HTTP_STATUS } from '@/constants'
import { useUIStore } from './useUIStore'

// Cart-related types
export interface CartRelatedProduct {
  id: number
  price?: number | string | null
  discount?: number | string
  discount_percent?: number | string
  to_campaign_product?: number
  to_variant?: number | null
}

export interface CartItem {
  id?: string
  campaign_product: {
    id: number
    name: string
    price: number | string
    image?: any
    currency?: string
    max_quantity?: number
    [key: string]: any
  }
  variant?: {
    id: number
    name?: string
    price_adjustment?: number
    options?: any[]
    [key: string]: any
  } | null
  quantity: number
  bundled_variants?: Array<{
    variant: {
      id: number
      name?: string
      [key: string]: any
    }
    quantity: number
  }> | null
  buy_now_clicked?: boolean
  related_product?: CartRelatedProduct | null
}

export interface Gateway {
  id: number | string
  public_key?: string
  [key: string]: any
}

export interface CartStore {
  // State
  cart: CartItem[]
  shipping: number | null
  tax: number | null
  gateway: Gateway | null
  cartIsOpen: boolean
  confirmCheckoutOpen: boolean
  checkoutErrors: string[] | null

  // Computed selectors
  cartItemCount: () => number
  cartTotal: () => number
  cartSubtotal: () => number
  shippingTotal: () => number
  taxTotal: () => number
  isCartEmpty: () => boolean
  hasShippingMethods: () => boolean

  // Actions
  setCart: (items: CartItem[]) => void
  addToCart: (item: CartItem) => void
  updateCartItem: (index: number, updates: Partial<CartItem>) => void
  removeFromCart: (index: number) => void
  clearCart: () => void
  setShipping: (amount: number | null) => void
  setTax: (amount: number | null) => void
  setGateway: (gateway: Gateway | null) => void
  setCartOpen: (open: boolean) => void
  setConfirmCheckoutOpen: (open: boolean) => void
  setCheckoutErrors: (errors: string[] | null) => void
  scheduleCartAutoClose: (ms?: number) => void
  cancelCartAutoClose: () => void

  // Async actions (with React Query integration)
  updateSessionCart: (sessionId: number, customerData?: any) => Promise<CartItem[]>
  addItemsToSessionCart: (sessionId: number, items: CartItem[]) => Promise<CartItem[]>
}

// Utility functions for cart calculations
const calculateCartSubtotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => {
    const basePrice = parseFloat(String(item.campaign_product.price)) || 0
    const variantAdjustment = item.variant?.price_adjustment || 0
    const itemTotal = (basePrice + variantAdjustment) * item.quantity
    return total + itemTotal
  }, 0)
}

// cart and checkout validation errors come back per cart line, under `items` (or `data.items` from checkout)
const getItemErrors = (errorData: any): any[] => {
  const itemErrors = errorData?.items ?? errorData?.data?.items
  return Array.isArray(itemErrors) ? itemErrors : []
}

// positions of the cart lines whose related product offer no longer exists
export const getInvalidRelatedLineIndexes = (errorData: any): number[] =>
  getItemErrors(errorData).reduce(
    (indexes: number[], itemError: any, index: number) => (itemError?.related_product ? [...indexes, index] : indexes),
    []
  )

export const getInvalidRelatedLineMessage = (errorData: any): string => {
  const itemError = getItemErrors(errorData).find((error: any) => error?.related_product)
  const message = itemError?.related_product
  return Array.isArray(message) ? message[0] : String(message)
}

const calculateCartItemCount = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + item.quantity, 0)
}

// Module-level auto-close timer — not in Zustand state because timer IDs can't be persisted
let cartAutoCloseTimer: ReturnType<typeof setTimeout> | null = null

export const useCartStore = create<CartStore>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        cart: [],
        shipping: null,
        tax: null,
        gateway: null,
        cartIsOpen: false,
        confirmCheckoutOpen: false,
        checkoutErrors: null,

        // Computed selectors
        cartItemCount: () => {
          const { cart } = get()
          return calculateCartItemCount(cart)
        },

        cartSubtotal: () => {
          const { cart } = get()
          return calculateCartSubtotal(cart)
        },

        shippingTotal: () => {
          const { shipping } = get()
          return shipping ?? 0
        },

        taxTotal: () => {
          const { tax } = get()
          return tax ?? 0
        },

        cartTotal: () => {
          const store = get()
          return store.cartSubtotal() + store.shippingTotal() + store.taxTotal()
        },

        isCartEmpty: () => {
          const { cart } = get()
          return cart.length === 0
        },

        hasShippingMethods: () => {
          const { shipping } = get()
          return shipping !== null
        },

        // Actions
        setCart: (items: CartItem[]) =>
          set(state => {
            state.cart = items
          }),

        addToCart: (item: CartItem) =>
          set(state => {
            // Check if item already exists in cart
            const existingIndex = state.cart.findIndex(
              cartItem =>
                cartItem.campaign_product.id === item.campaign_product.id &&
                cartItem.variant?.id === item.variant?.id &&
                cartItem.related_product?.id === item.related_product?.id
            )

            if (existingIndex >= 0) {
              // Update quantity if item exists
              state.cart[existingIndex].quantity += item.quantity
            } else {
              // Add new item to cart
              state.cart.push(item)
            }
          }),

        updateCartItem: (index: number, updates: Partial<CartItem>) =>
          set(state => {
            if (index >= 0 && index < state.cart.length) {
              state.cart[index] = { ...state.cart[index], ...updates }
            }
          }),

        removeFromCart: (index: number) =>
          set(state => {
            if (index >= 0 && index < state.cart.length) {
              state.cart.splice(index, 1)
            }
          }),

        clearCart: () =>
          set(state => {
            state.cart = []
            state.shipping = null
            state.tax = null
            state.checkoutErrors = null
          }),

        setShipping: (amount: number | null) =>
          set(state => {
            state.shipping = amount
          }),

        setTax: (tax: number | null) =>
          set(state => {
            state.tax = tax
          }),

        setGateway: (gateway: Gateway | null) =>
          set(state => {
            state.gateway = gateway
          }),

        setCartOpen: (open: boolean) => {
          // Any manual open/close cancels a pending auto-close
          if (cartAutoCloseTimer) {
            clearTimeout(cartAutoCloseTimer)
            cartAutoCloseTimer = null
          }
          set(state => {
            state.cartIsOpen = open
          })
        },

        setConfirmCheckoutOpen: (open: boolean) =>
          set(state => {
            state.confirmCheckoutOpen = open
          }),

        setCheckoutErrors: (errors: string[] | null) =>
          set(state => {
            state.checkoutErrors = errors
          }),

        scheduleCartAutoClose: (ms = 6000) => {
          if (cartAutoCloseTimer) {
            clearTimeout(cartAutoCloseTimer)
          }
          cartAutoCloseTimer = setTimeout(() => {
            cartAutoCloseTimer = null
            set(state => {
              state.cartIsOpen = false
            })
          }, ms)
        },

        cancelCartAutoClose: () => {
          if (cartAutoCloseTimer) {
            clearTimeout(cartAutoCloseTimer)
            cartAutoCloseTimer = null
          }
        },

        updateSessionCart: async (sessionId: number, customerData: any = {}) => {
          const { cart } = get()

          // Normalize cart data
          const cartMap: Record<string, CartItem> = {}
          const normalizedCart: CartItem[] = []
          const params: any[] = []

          if (cart && cart.length) {
            cart.forEach(item => {
              if (item.quantity > 0) {
                const key = `${item.campaign_product.id}-${item.variant?.id || 0}-${item.related_product?.id || 0}`

                if (key in cartMap) {
                  cartMap[key].quantity += item.quantity
                } else {
                  cartMap[key] = item
                }
              }
            })

            Object.values(cartMap).forEach(item => {
              normalizedCart.push(item)

              let bundledVariants = null
              if (item.bundled_variants?.length) {
                bundledVariants = item.bundled_variants.map(bundle => ({
                  variant: bundle.variant.id,
                  quantity: bundle.quantity,
                }))
              }

              params.push({
                campaign_product: item.campaign_product.id,
                variant: item.variant?.id || null,
                quantity: item.quantity,
                bundled_variants: bundledVariants,
                buy_now_clicked: item.buy_now_clicked || false,
                related_product: item.related_product?.id || null,
              })
            })
          }

          try {
            const cartData = await sessionCartService.updateSessionCart(sessionId, {
              items: params,
              customer: customerData,
            })

            const updatedItems = cartData.items || []
            get().setCart(updatedItems)

            // Parse shipping and tax from API response
            if (cartData.shipping) {
              const shippingVal = Array.isArray(cartData.shipping)
                ? cartData.shipping.reduce((sum: number, s: any) => sum + (parseFloat(s.price) || 0), 0)
                : parseFloat(String(cartData.shipping)) || 0
              get().setShipping(shippingVal)
            }
            if (cartData.tax != null) {
              get().setTax(parseFloat(String(cartData.tax)) || 0)
            }
            if (cartData.gateway) {
              get().setGateway(cartData.gateway as unknown as Gateway)
            }

            return updatedItems
          } catch (error: any) {
            const errorData = error?.response?.data
            const invalidIndexes =
              error?.response?.status === HTTP_STATUS.BAD_REQUEST ? getInvalidRelatedLineIndexes(errorData) : []

            // drop the lines whose offer is gone, tell the customer, and re-sync the remaining cart
            if (invalidIndexes.length) {
              get().setCart(normalizedCart.filter((_, index) => !invalidIndexes.includes(index)))
              useUIStore.getState().setError(getInvalidRelatedLineMessage(errorData))
              return get().updateSessionCart(sessionId, customerData)
            }

            console.error('Failed to update session cart:', error)
            throw error
          }
        },

        addItemsToSessionCart: async (sessionId: number, items: CartItem[]) => {
          const { cart } = get()

          // Add items to current cart
          const newCart = [...cart]
          if (!Array.isArray(items)) {
            items = [items as CartItem]
          }
          newCart.unshift(...items)

          // Update the session with new cart
          get().setCart(newCart)
          return get().updateSessionCart(sessionId)
        },
      })),
      {
        name: 'ivrpath-cart',
        storage: createJSONStorage(() => localStorage),
        // Persist cart state
        partialize: state => ({
          cart: state.cart,
          cartIsOpen: state.cartIsOpen,
          // Don't persist shipping/tax as they're session-dependent
        }),
      }
    ),
    { name: 'CartStore', enabled: import.meta.env.DEV }
  )
)

// Selector hooks for common use cases
export const useCart = () => useCartStore(state => state.cart)
export const useCartItemCount = () => useCartStore(state => state.cartItemCount())
export const useCartTotal = () => useCartStore(state => state.cartTotal())
export const useCartSubtotal = () => useCartStore(state => state.cartSubtotal())
export const useIsCartEmpty = () => useCartStore(state => state.isCartEmpty())
export const useCartIsOpen = () => useCartStore(state => state.cartIsOpen)
export const useShippingMethods = () => useCartStore(state => state.shipping)
export const useCartGateway = () => useCartStore(state => state.gateway)
